import { ReactElement } from "react";
import Head from "next/head";
import { MainLayout } from "@/containers/layout/LandingLayout";
import routes from "@/constants/routes";
import { getArticles } from "@/services/cms/api";
import { ARTICLES_DEFAULT_POPULATE } from "@/components/blog/articleList/ArticleList";
import { Article } from "@/services/cms/apiTypes";
import { SubLayout } from "@/containers/layout/LandingLayout";
import { ArticleContent } from "@/components/blog/articleContent/articleContent";

interface BlogProps {
  article: Article;
}
const BlogPage = ({ article }: BlogProps): JSX.Element => {
  return (
    <SubLayout allowOverflow>
      <Head>
        <title>{article.attributes.title}</title>
        <meta name="description" key="description" content={article.attributes.description} />
        <meta name="twitter:card" content="Keyzy" key="twcard" />
        <meta name="twitter:creator" content="keyzyhomes" key="twhandle" />
        <meta property="og:url" content={article.attributes.slug} key="ogurl" />
        <meta property="og:type" content="article" key="ogtype" />
        <meta
          property="og:image"
          content={article?.attributes?.cover?.data?.attributes?.formats?.large?.url}
          key="ogimage"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Keyzy" key="ogsitename" />
        <meta property="og:title" content={article.attributes.title} key="ogtitle" />
        <meta property="og:description" content={article.attributes.description} key="ogdesc" />
        <meta property="og:locale" content="en_GB" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://keyzy.com/" },
                { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://keyzy.com/blog" },
                { "@type": "ListItem", "position": 3, "name": article.attributes.title, "item": `https://keyzy.com/blog/${article.attributes.slug}` },
              ],
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BlogPosting",
              "headline": article.attributes.title,
              "description": article.attributes.description,
              "datePublished": article.attributes.addedOn,
              "url": `https://www.keyzy.com/blog/${article.attributes.slug}`,
              "image": article.attributes.cover?.data?.attributes?.formats?.large?.url,
              ...(article.attributes.author?.data && {
                "author": {
                  "@type": "Person",
                  "name": article.attributes.author.data.attributes.name,
                },
              }),
              "publisher": {
                "@type": "Organization",
                "name": "Keyzy",
                "url": "https://www.keyzy.com",
                "logo": "https://www.keyzy.com/keyzy-logo.png",
              },
            }),
          }}
        />
      </Head>
      <ArticleContent article={article} baseUrl={routes.blog}></ArticleContent>
    </SubLayout>
  );
};

BlogPage.getLayout = function getLayout(page: ReactElement) {
  return <MainLayout theme="light">{page}</MainLayout>;
};
export async function getServerSideProps(context: { query: { slug: string } }) {
  const { slug } = context.query;
  const articleResponse = await getArticles({
    populate: [
      ...ARTICLES_DEFAULT_POPULATE,
      "nextArticle",
      "nextArticle.cover",
      "nextArticle.author",
      "nextArticle.author.avatar",
    ],
    filters: {
      slug: {
        $eq: slug,
      },
    },
  });
  if (articleResponse.data.length === 1) {
    return {
      props: { article: articleResponse.data[0] },
    };
  }
  return {
    notFound: true,
  };
}
export default BlogPage;
